// "Enable desktop notifications" — the only place permission is ever asked.
//
// ⚠️ The browser prompt can be shown ONCE and a denial is sticky, undoable
// only through site settings most people never find. So it is asked from this
// click and nowhere else: not on load, not on login, and not as a side effect
// of installing the app. Installing is not consent to be notified.
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  browserSupports, currentSubscription, disable, enable, fetchVapidKey, reconcile, resolveState,
  type DesktopPushState,
} from './desktopNotifications';

export const DesktopNotificationsPanel: React.FC = () => {
  const [state, setState] = useState<DesktopPushState | null>(null);
  const [detail, setDetail] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const supported = browserSupports();
    if (!supported) { setState('unsupported'); return; }
    // ⚠️ Reading the permission does NOT prompt. Only requestPermission does,
    // and that is behind the button below.
    const permission = typeof Notification !== 'undefined' ? Notification.permission : null;
    const [key, sub] = await Promise.all([
      fetchVapidKey().catch(() => ({ enabled: false, public_key: '' })),
      currentSubscription().catch(() => null),
    ]);
    const next = resolveState(supported, !!key.enabled, permission, !!sub);
    setState(next);
    // ⚠️ "On for this browser" is read from the LOCAL subscription, which can
    // exist while the server has no row for it — and then nothing is ever
    // delivered while the panel claims it is on. Re-sending the subscription
    // is an idempotent upsert on the endpoint, so it either confirms or
    // repairs. Done after the state is set so the panel never waits on it.
    if (next === 'on') void reconcile();
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const onEnable = async () => {
    setBusy(true); setDetail(undefined);
    try {
      const res = await enable();
      setState(res.state);
      setDetail(res.detail);
      if (res.state === 'on') toast.success('Desktop notifications are on for this browser.');
    } catch (e) {
      setState('error');
      setDetail(getApiErrorMessage(e, 'Could not turn these on.'));
    } finally { setBusy(false); }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await disable();
      setState('off');
      toast.success('Desktop notifications are off for this browser.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not turn these off.'));
    } finally { setBusy(false); }
  };

  // Nothing to offer and nothing useful to say: a deployment without VAPID
  // keys, or a browser that cannot do this at all. Rendering an explanation
  // for something they never asked about is noise.
  if (state === null || state === 'unavailable') return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5" /> Desktop notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {state === 'unsupported' && (
          <p className="text-sm text-muted-foreground">
            This browser cannot show desktop notifications. You will still get everything
            in the app and by email.
          </p>
        )}

        {state === 'blocked' && (
          // ⚠️ We cannot ask again — the button would do nothing. Say where
          // the switch actually lives instead of offering a dead control.
          <p className="text-sm text-muted-foreground">
            Notifications are blocked for this site, so we cannot ask again from here.
            Allow them in your browser’s site settings (the icon at the left of the address
            bar), then reload this page.
          </p>
        )}

        {(state === 'off' || state === 'error') && (
          <>
            <p className="text-sm text-muted-foreground">
              Get mentions, assignments and approvals on this computer while KaziPeople is
              open. Your browser will ask for permission once.
            </p>
            {state === 'error' && (
              <p className="text-sm text-danger">
                {detail || 'Could not subscribe this browser.'} Nothing was turned on.
              </p>
            )}
            <Button onClick={onEnable} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <Bell className="mr-2 h-4 w-4" aria-hidden />}
              Enable desktop notifications
            </Button>
          </>
        )}

        {state === 'on' && (
          <>
            <p className="text-sm text-muted-foreground">
              On for this browser. Choose which kinds to receive under <strong>Desktop</strong>{' '}
              in Notifications — nothing financial is sent to a desktop notification.
            </p>
            <Button variant="outline" onClick={onDisable} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <BellOff className="mr-2 h-4 w-4" aria-hidden />}
              Turn off for this browser
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DesktopNotificationsPanel;
